import { useEffect, useMemo, useState } from 'react';
import { BaseOption } from 'ui/selectv2/types';
import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import { LIST_TYPES } from 'constants/listTypes';
import { GraphQLAudienceList, useQueryAudienceLists } from 'modules/campaigns/editor/hooks';
import { recencyInDays } from 'utils/datetime/date';
import {
  useGetAnonUserCriteriaSettings,
  useUpdateUserRetentionControls,
} from '../../hooks/useAnonUserCriteriaSettings';
import { CriteriaRetentionContext } from './CriteriaRetentionContext';
import { CriteriaRetentionContextState } from './CriteriaRetentionContext';
import { DEFAULT_SETTINGS } from './constants';

export const CriteriaRetentionContextProvider = ({
  children,
  isOpenByDefault = false, // for testing
}: { children: React.ReactNode; isOpenByDefault?: boolean }) => {
  // Data fetching
  const anonUserCriteriaSettingsData = useGetAnonUserCriteriaSettings();

  const {
    data,
    error: hasCriteriaSettingsError,
    isLoading: isLoadingSettings,
    refetch: refetchCriteriaSettings,
  } = anonUserCriteriaSettingsData;

  // Query audience lists
  const audienceListVariables = {
    search: '',
    sort: {
      sortBy: 'UpdatedAt',
      sortDirection: SORT_DIRECTIONS.DESCENDING,
    },
    filterInfo: {
      listType: [LIST_TYPES.STANDARD, LIST_TYPES.DYNAMIC],
    },
    pagination: {
      offset: 0,
      limit: 1000,
    },
    isEmbedded: false,
  };

  const {
    results: audienceLists,
    errorMessage: listsErrorMessage,
    hasError: hasListsError,
    isLoading: isLoadingLists,
  } = useQueryAudienceLists(audienceListVariables);

  // Create an audience list dictionary
  const audienceListsMap = useMemo(() => {
    return (
      audienceLists?.reduce(
        (acc, list) => {
          acc[list.id] = list;
          return acc;
        },
        {} as Record<number, GraphQLAudienceList>,
      ) ?? {}
    );
  }, [audienceLists]);

  const anonUserCriteriaSettings = data?.getAnonUserCriteriaSettings?.setting;
  const isEnabled =
    anonUserCriteriaSettings?.enableUserRetention ?? DEFAULT_SETTINGS.isRetentionEnabled;
  const ageInDays = `${anonUserCriteriaSettings?.ageInDays ?? DEFAULT_SETTINGS.selectedRetentionOption}`;
  const excludedListIds =
    anonUserCriteriaSettings?.excludedListIds ?? DEFAULT_SETTINGS.selectedListOptions;

  const getListOptions = (listIds: number[]) => {
    if (!listIds?.length) return [];
    if (!Object.keys(audienceListsMap).length) return [];
    return (
      listIds?.map((listId) => ({
        id: audienceListsMap[listId]?.id,
        label: `${audienceListsMap[listId]?.name} (${audienceListsMap[listId]?.listType})`,
        description: `List ID: ${audienceListsMap[listId]?.id} • ${audienceListsMap[listId]?.listType} • Updated ${Math.abs(recencyInDays(audienceListsMap[listId]?.updatedAt))} days ago`,
        value: `${audienceListsMap[listId]?.id}`,
      })) || []
    );
  };

  const initialSelectedListOptions = getListOptions(anonUserCriteriaSettings?.excludedListIds);

  // State management
  const [isCriteriaRetentionTakeoverOpen, setIsCriteriaRetentionTakeoverOpen] =
    useState(isOpenByDefault);

  const [isRetentionEnabled, setIsRetentionEnabled] = useState(isEnabled);
  const [selectedListOptions, setSelectedListOptions] = useState<BaseOption[]>(
    initialSelectedListOptions,
  );
  const [selectedRetentionOption, setSelectedRetentionOption] = useState<string>(ageInDays);

  useEffect(() => {
    if (!isCriteriaRetentionTakeoverOpen) return;

    setIsRetentionEnabled(
      anonUserCriteriaSettings?.enableUserRetention ?? DEFAULT_SETTINGS.isRetentionEnabled,
    );
    setSelectedListOptions(
      getListOptions(
        anonUserCriteriaSettings?.excludedListIds ?? DEFAULT_SETTINGS.selectedListOptions,
      ),
    );
    setSelectedRetentionOption(
      `${anonUserCriteriaSettings?.ageInDays}` || DEFAULT_SETTINGS.selectedRetentionOption,
    );
  }, [
    anonUserCriteriaSettings?.enableUserRetention,
    anonUserCriteriaSettings?.excludedListIds,
    anonUserCriteriaSettings?.ageInDays,
    isCriteriaRetentionTakeoverOpen,
  ]);

  const onSuccess = () => {
    setIsCriteriaRetentionTakeoverOpen(false);
  };

  // Submit mutation
  const updateUserRetentionControls = useUpdateUserRetentionControls(onSuccess);

  // Handlers
  const handleSelect = (value: string) => {
    setSelectedRetentionOption(value);
  };

  const handleListSelection = (options: BaseOption[]) => {
    setSelectedListOptions(options);
  };

  const handleSave = () => {
    const variables = {
      enableUserRetention: isRetentionEnabled,
      ageInDays: isRetentionEnabled
        ? Number(selectedRetentionOption)
        : Number(DEFAULT_SETTINGS.selectedRetentionOption),
      excludedListIds: isRetentionEnabled
        ? selectedListOptions.map((option) => Number(option.value))
        : DEFAULT_SETTINGS.selectedListOptions,
    };

    updateUserRetentionControls({ variables });
  };

  // Derived data
  const listOptions = getListOptions(audienceLists?.map((list) => list.id) ?? []);

  const value: CriteriaRetentionContextState = {
    isOpen: isCriteriaRetentionTakeoverOpen,
    setIsOpen: setIsCriteriaRetentionTakeoverOpen,
    anonUserCriteriaSettings,
    hasCriteriaSettingsError: hasCriteriaSettingsError,
    isLoadingSettings: isLoadingSettings,
    isRetentionEnabled,
    setIsRetentionEnabled,
    excludedListIds,
    selectedListOptions,
    setSelectedListOptions,
    selectedRetentionOption,
    handleSelect,
    handleListSelection,
    listOptions,
    isLoadingLists,
    hasListsError,
    listsErrorMessage,
    handleSave,
    refetchCriteriaSettings,
  };

  return (
    <CriteriaRetentionContext.Provider value={value}>{children}</CriteriaRetentionContext.Provider>
  );
};
